<div cdkDropList [cdkDropListDisabled]="!userService.admin()" (cdkDropListDropped)="onStageDrop($event)"
  class="flex flex-col gap-4">
  @for(stage of projectStages; track stage.projectStageId) {

  @let stageId = stage.projectStageId!;
  @let stageBudget = stage.estimatedHours || 0;
  @let stageEncoded = getStageEncodedHours(stageId);
  @let stageEncodedDiff = stageBudget - stageEncoded;
  @let isDone = stage.status === 'DONE';

  <div cdkDrag [cdkDragDisabled]="!userService.admin()"
    class="group/phase bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">

    <!-- Phase Header -->
    <div class="flex items-center gap-3 px-5 py-4">

      <!-- Drag handle (admin, on hover) -->
      @if(userService.admin()) {
      <div cdkDragHandle
        class="cursor-move text-gray-400 hover:text-gray-600 transition-all shrink-0 -ml-1.5">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
        </svg>
      </div>
      }

      <!-- Collapse chevron -->
      <button (click)="toggleCollapse(stageId, $event)"
        class="text-gray-400 hover:text-gray-700 transition-colors shrink-0 cursor-pointer">
        <svg class="w-4 h-4 transition-transform" [class.-rotate-90]="isCollapsed(stageId)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <!-- Left group: dot + name + status -->
      <div class="flex items-center gap-2.5 min-w-0">
        @if(userService.admin()) {
        <label class="relative cursor-pointer shrink-0 flex items-center">
          <input type="color" [value]="stage.color"
            (input)="onStageFieldChange(stage.projectStageId!, 'color', $any($event.target).value)"
            class="absolute inset-0 w-3 h-3 opacity-0 cursor-pointer">
          <span class="block w-2.5 h-2.5 rounded-full hover:scale-125 transition-transform" [style.background]="stage.color"></span>
        </label>
        } @else {
        <span class="block w-2.5 h-2.5 rounded-full shrink-0" [style.background]="stage.color"></span>
        }

        @if(editingStageId === stage.projectStageId && userService.admin()) {
        <input type="text" [(ngModel)]="editedStageName" (blur)="saveStageName(stage.projectStageId!)"
          (keydown.enter)="saveStageName(stage.projectStageId!)" (keydown.escape)="cancelEditStageName()"
          class="text-sm font-bold text-gray-900 border-2 border-blue-500 rounded-lg px-2 py-0.5 focus:outline-none min-w-0">
        } @else {
        <h3 class="text-sm font-bold text-gray-900 truncate">{{ stage.stageName }}</h3>
        @if(userService.admin()) {
        <button (click)="startEditStageName(stage)"
          class="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all cursor-pointer shrink-0">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        }
        }

        <!-- Status -->
        @if(userService.admin()) {
        <select [(ngModel)]="stage.status" (ngModelChange)="onStageFieldChange(stage.projectStageId!, 'status', $event)"
          [ngClass]="getStageStatusClasses(stage.status)"
          class="status-pill appearance-none text-center px-2.5 py-1 rounded-full border-none cursor-pointer shrink-0">
          <option value="NOTBEGIN">New</option>
          <option value="INPROGRESS">In Progress</option>
          <option value="DONE">Done</option>
        </select>
        } @else {
        <span [ngClass]="getStageStatusClasses(stage.status)"
          class="status-pill text-center px-2.5 py-1 rounded-full shrink-0">{{ getStageStatusLabel(stage.status) }}</span>
        }
      </div>

      <!-- Spacer -->
      <div class="flex-1 min-w-0"></div>

      <!-- Right group: dates + progress + hours + add task -->
      <div class="flex items-center gap-5 shrink-0">
        <!-- Dates -->
        <span class="hidden sm:inline-flex items-center gap-1.5 text-xs text-gray-500 whitespace-nowrap justify-end w-32">
          @if(stage.startDate && stage.endDate) {
          {{ stage.startDate | date:'d MMM' }} <span class="text-gray-300">→</span> {{ stage.endDate | date:'d MMM' }}
          }
        </span>

        <!-- Progress bar -->
        <div class="hidden md:block w-28 h-1.5 rounded-full bg-gray-200 overflow-hidden">
          @if(stageBudget > 0) {
          <div class="h-full rounded-full transition-all" [class.bg-emerald-500]="isDone" [class.bg-blue-500]="!isDone"
            [style.width.%]="getStageProgress(stageId)"></div>
          }
        </div>

        <!-- Hours -->
        <span class="text-xs whitespace-nowrap font-medium w-20 text-right">
          @if (stageBudget > 0 || stageEncoded > 0) {
          <span [class.text-red-500]="stageBudget > 0 && stageEncodedDiff < 0" [class.text-emerald-600]="isDone" [class.text-gray-700]="!isDone && stageEncodedDiff >= 0">{{ stageEncoded | number:'1.0-1' }}h</span>
          @if (stageBudget > 0) { <span class="text-gray-400"> / {{ stageBudget }}h</span> }
          }
        </span>

        <!-- Add Task -->
        <button (click)="onAddTask(stage.projectStageId!)"
          class="inline-flex items-center gap-1 px-2.5 py-1 text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-100 hover:border-blue-200 rounded-md text-xs font-semibold transition-colors cursor-pointer">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Task
        </button>
      </div>

      <!-- Link + Menu (admin, on hover) -->
      <div class="flex items-center gap-0.5 shrink-0">
        @if(canManageStage(stage.projectStageId!)) {
        <button (click)="toggleAddStageLink(stage.projectStageId!)"
          class="p-1.5 text-gray-500 bg-white border border-gray-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 rounded-lg transition-colors cursor-pointer" title="Add link">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </button>
        }

        @if(userService.admin()) {
        <div class="relative">
          <button (click)="toggleMenu(stage.projectStageId!, $event)"
            class="p-1.5 text-gray-500 bg-white border border-gray-200 hover:bg-gray-100 hover:text-gray-700 rounded-lg transition-colors cursor-pointer">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01" />
            </svg>
          </button>
          @if(openMenuId === stage.projectStageId) {
          <div class="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
            <button (click)="editStage.emit(stage); openMenuId = undefined"
              class="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit Phase
            </button>
            <button (click)="deleteStageProject(stage.projectStageId!)"
              class="w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50 flex items-center gap-2 cursor-pointer">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Phase
            </button>
          </div>
          }
        </div>
        }
      </div>
    </div>

    <!-- Links -->
    @if(stage.links && stage.links.length > 0 || showAddLinkFormForStage === stage.projectStageId) {
    <div class="px-5 pb-3 -mt-1">
      @if(showAddLinkFormForStage === stage.projectStageId) {
      <div class="mb-2 p-2.5 bg-blue-50 rounded-lg border border-blue-200 flex gap-2 items-end flex-wrap">
        <select [(ngModel)]="newStageLinkType" (ngModelChange)="onStageLinkTypeChange()"
          class="px-2 py-1 text-xs border border-gray-300 rounded-md outline-none bg-white cursor-pointer">
          <option value="Drive">Drive</option>
          <option value="Other">Other</option>
        </select>
        <input type="text" [(ngModel)]="newStageLinkName" [readonly]="newStageLinkType === 'Drive'" placeholder="Name"
          class="px-2 py-1 text-xs border border-gray-300 rounded-md outline-none flex-1 min-w-[80px]"
          [class.bg-gray-100]="newStageLinkType === 'Drive'">
        <input type="url" [(ngModel)]="newStageLinkUrl" (keydown.enter)="addStageLink(stage.projectStageId!)"
          (keydown.escape)="cancelAddStageLink()" placeholder="https://..."
          class="px-2 py-1 text-xs border border-gray-300 rounded-md outline-none flex-1 min-w-[120px]">
        <button (click)="addStageLink(stage.projectStageId!)" [disabled]="!newStageLinkUrl || !newStageLinkName"
          class="px-2.5 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50 cursor-pointer">Add</button>
        <button (click)="cancelAddStageLink()"
          class="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-md hover:bg-gray-300 cursor-pointer">✕</button>
      </div>
      }
      @if(stage.links && stage.links.length > 0) {
      <div class="flex flex-wrap gap-1.5 pl-8">
        @for(link of stage.links; track $index) {
        <div class="group inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 border border-gray-200 rounded-md hover:border-blue-300 hover:bg-blue-50 transition-all text-xs">
          <a [href]="getLinkUrl(link)" (click)="openExternalLink(getLinkUrl(link), $event)" target="_blank"
            rel="noopener noreferrer" class="text-blue-600 hover:underline truncate max-w-[150px] cursor-pointer">
            {{ link.name || getLinkDisplayName(link) }}
          </a>
          @if(canManageStage(stage.projectStageId!)) {
          <button (click)="removeStageLink(stage.projectStageId!, $index)"
            class="opacity-0 group-hover:opacity-100 p-0.5 text-red-500 hover:bg-red-100 rounded transition-all cursor-pointer">
            <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          }
        </div>
        }
      </div>
      }
    </div>
    }

    <!-- Tasks -->
    @if(!isCollapsed(stageId)) {
    <div class="border-t border-gray-100 pb-1 rounded-b-xl overflow-hidden">
      <app-list-task [projectStageId]="stage.projectStageId!" [projectManagerUserIds]="projectManagerUserIds"
        [filteredTasks]="allTasks" (taskDetailOpen)="onTaskDetailOpen($event)">
      </app-list-task>
    </div>
    }
  </div>
  }
  @empty {
  <div class="text-center py-12">
    <svg class="w-10 h-10 text-gray-200 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
    <p class="text-gray-500 text-sm mb-1">No phases yet</p>
    <p class="text-xs text-gray-400">Add your first phase to get started</p>
  </div>
  }
</div>
@if(showConfirm){
<app-confirm-modal [message]="confirmMessage" (close)="handleConfirm($event)"></app-confirm-modal>
}
